import {
  AsyncPipe,
  DatePipe,
  DOCUMENT,
  I18nPluralPipe,
  Location,
  NgClass,
  NgTemplateOutlet,
  SlicePipe,
  UpperCasePipe,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core';
import { WINDOW } from '@ng-web-apis/common';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  from,
  map,
  mergeMap,
  NEVER,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { AppShellNoRenderDirective } from 'src/app/common/app-shell-render/app-shell-no-render.directive';
import { AttachedIconComponent } from 'src/app/_shared/components/attached-icon/attached-icon.component';
import { AvatarDirective } from 'src/app/common/avatar/avatar.directive';
import { BasePlusComponent } from 'src/app/_shared/components/base/base-plus.component';
import { CardCarouselComponent } from 'src/app/_shared/components/card-carousel/card-carousel.component';
import { CherryBoxComponent } from 'src/app/_shared/components/cherry-box/cherry-box.component';
import {
  FollowFriendGQL,
  UnfollowFriendGQL,
} from 'src/app/_shared/dialogs/people-dialog/people-dialog.mutation.generated';
import { EllipsisTooltipDirective } from 'src/app/common/ellipsis-tooltip/ellipsis-tooltip.directive';
import { EntityTitleComponent } from 'src/app/_shared/components/entity-title/entity-title.component';
import { HeaderContentComponent } from 'src/app/_shared/components/header/header-content/header-content.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { LoadingComponent } from 'src/app/_shared/components/loading/loading.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { NotLoggedService } from 'src/app/common/not-logged/not-logged.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { PeopleAvatarListComponent } from 'src/app/_shared/components/people-avatar-list/people-avatar-list.component';
import { PeopleLikesComponent } from 'src/app/_shared/components/people-likes/people-likes.component';
import { PostCardComponent } from 'src/app/_shared/components/post-card/post-card.component';
import { SearchService } from 'src/app/_shared/components/prefix-search/search.service';
import { ReportComponent } from 'src/app/_shared/components/report/report.component';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { ServerStoreService } from 'src/app/common/store/server-store.service';
import { TypedTemplateDirective } from 'src/app/common/typed-template/typed-template.directive';
import { UserNamePipe } from 'src/app/common/user-name/user-name.pipe';
import { UserNameService } from 'src/app/common/user-name/user-name.service';
import { UserService } from 'src/app/common/user/user.service';
import {
  addHttp,
  baseUrl,
  capacitorDownloadFile,
  cdn,
  daysFromDate,
  getDefaultModalSettings,
  mailTelTo,
  mergeDeep,
  removeHttp,
  ScreenSizes,
} from 'src/app/common/utils/helpers';
import {
  CarouselableEntityEnum,
  ContentLike,
  Follow,
  Image,
  Locale,
  OperationReturnTypeEnum,
  ReportableEntityEnum,
  SharrySelection,
  User,
  UserGalleryImage,
} from 'src/types/graphql';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { TranslationRequestGQL } from '../community/community.mutation.generated';
import { LandEssentialsGQL } from '../content/content.query.generated';
import {
  GetUserAdditionalInfoBySlugGQL,
  GetUserBySlugGQL,
  GetUserBySlugQuery,
  GetUserBySlugTranslatedGQL,
  GetUserFollowersGQL,
  GetUserFollowingsGQL,
  GetUserMyFollowGQL,
} from './profile.query.generated';
import { GtagService } from 'src/app/common/gtag/gtag.service';
import { RouteTranslatePipe } from '../../_shared/pipes/route-translate.pipe';
import { SafePipe } from '../../_shared/pipes/safe.pipe';
import { SlugPipe } from '../../_shared/pipes/slug.pipe';
import { ContentTypeColorizerPipe } from '../../_shared/pipes/content-type-colorizer.pipe';

@Component({
  selector: 'sharry-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: true,
  providers: [ServerStoreService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    UpperCasePipe,
    AsyncPipe,
    I18nPluralPipe,
    SlicePipe,
    DatePipe,
    I18NextModule,
    RouteTranslatePipe,
    MatButtonModule,
    LoadingComponent,
    MatDialogModule,
    MatSnackBarModule,
    ReportComponent,
    CardCarouselComponent,
    UserNamePipe,
    RouterLink,
    SafePipe,
    AvatarDirective,
    IconComponent,
    PeopleLikesComponent,
    TypedTemplateDirective,
    MatTabsModule,
    SlugPipe,
    EllipsisTooltipDirective,
    ContentTypeColorizerPipe,
    MatPaginatorModule,
    EntityTitleComponent,
    CherryBoxComponent,
    PeopleAvatarListComponent,
    ReportComponent,
    AppShellNoRenderDirective,
    PostCardComponent,
    AttachedIconComponent,
    FooterComponent,
    HeaderContentComponent,
  ],
})
export class ProfileComponent extends BasePlusComponent implements OnInit, AfterViewInit, OnDestroy {
  typeToken: {
    user: UserExtended;
  };
  params: Params;
  queryParams: any = undefined;
  contentBusy = false;
  addHttp = addHttp;
  removeHttp = removeHttp;
  capacitorDownloadFile = capacitorDownloadFile;
  daysFromDate = daysFromDate;
  headerIntersecting = false;
  posterImageClicked = false;
  reportableEntityEnum = ReportableEntityEnum;
  collaboratedWonderBusy = false;
  paladinWonderBusy = false;
  sharryBusy = false;
  eventBusy = false;
  networkBusy = false;
  postsBusy = false;
  followerBusy = false;
  contentClappingsBusy = false;
  userFollowingsBusy = false;
  onFollowBusy = false;
  usefulCollapsed = true;
  maxPictures = 0;
  limit$: Observable<number>;
  sharrySelection = SharrySelection;
  busy = false;
  itemPluralMapping = {
    followers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '=1': 'profile.follow-this-page',
      other: 'profile.follow-this-page.plural',
    },
    followings: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '=1': 'profile.following-this-page',
      other: 'profile.following-this-page.plural',
    },
  };
  seeTranslated = false;
  cdn = cdn;
  slug: string;
  private limitSubj: BehaviorSubject<number>;

  constructor(
    public userService: UserService,
    private getUserBySlugGQL: GetUserBySlugGQL,
    private getUserFollowingsGQL: GetUserFollowingsGQL,
    private getUserAdditionalInfoBySlugGQL: GetUserAdditionalInfoBySlugGQL,
    private getUserFollowersGQL: GetUserFollowersGQL,
    private getUserMyFollowGQL: GetUserMyFollowGQL,
    private notLoggedService: NotLoggedService,
    private route: ActivatedRoute,
    private router: Router,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private routeTranslateService: RouteTranslateService,
    @Inject(I18NEXT_SERVICE) public translateService: ITranslationService,
    private openDialogService: OpenDialogService,
    private renderer: Renderer2,
    private dialog: MatDialog,
    private searchService: SearchService,
    public profileStore: ServerStoreService<UserExtended>,
    private cd: ChangeDetectorRef,
    private userNameService: UserNameService,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private location: Location,
    private gtagService: GtagService,
    private followFriendGQL: FollowFriendGQL,
    private translationRequest: TranslationRequestGQL,
    private getUserBySlugTranslated: GetUserBySlugTranslatedGQL,
    private unfollowFriendGQL: UnfollowFriendGQL,
    private landEssentialsGQL: LandEssentialsGQL,
  ) {
    super();
    this.slug = this.location
      .path(false)
      .substring(this.location.path(false).lastIndexOf('/') + 1)
      .split('?')[0];
    this.limitSubj = new BehaviorSubject(
      this.isMobileNotIpad()
        ? this.isCustomSmallScreen(367)
          ? 2
          : 3
        : this.isMobileIpad()
          ? 5
          : this.isCustomSmallScreen(1067)
            ? 4
            : this.isCustomSmallScreen(1152)
              ? 5
              : 6,
    );
    this.limit$ = this.limitSubj.asObservable();
  }

  @HostListener('window:resize') onResize() {
    this.limitSubj.next(
      this.isMobileNotIpad()
        ? this.isCustomSmallScreen(367)
          ? 2
          : 3
        : this.isMobileIpad()
          ? 5
          : this.isCustomSmallScreen(1067)
            ? 4
            : this.isCustomSmallScreen(1152)
              ? 5
              : 6,
    );
  }

  ngOnInit() {
    this.slug = this.location
      .path(false)
      .substring(this.location.path(false).lastIndexOf('/') + 1)
      .split('?')[0];
    this.subscriptions.push(
      combineLatest([this.route.params, this.route.queryParams])
        .pipe(
          mergeMap(([params, queryParams]) => {
            this.queryParams = queryParams;
            this.params = params;
            this.slug = params?.slug;
            const key = 'PROFILE_TRANSFER_STATE_' + params?.slug + '_' + this.translateService.language;
            this.dialog.closeAll();
            this.profileStore.init(key);
            if (!this.profileStore?.state) {
              this.busy = true;
              this.cd.detectChanges();
              return this.getUserBySlugGQL.fetch(
                { slug: this.params?.slug || this.userService?.user?.slug },
                { fetchPolicy: 'no-cache' },
              );
            } else {
              this.fetchUser(this.profileStore?.state, true);
              return this.getUserMyFollowGQL.fetch({ slug: this.params?.slug || this.userService?.user?.slug }).pipe(
                mergeMap(r => {
                  this.fetchUser(mergeDeep(this.profileStore?.state, r?.data?.userBySlug as any), true);
                  return NEVER;
                }),
              );
            }
          }),
          catchError(() => of({ data: { userBySlug: null } } as ApolloQueryResult<GetUserBySlugQuery>)),
          mergeMap(res => {
            if (res?.data?.userBySlug) {
              this.fetchUser(res?.data?.userBySlug as any, false);
              this.busy = false;
              this.cd.detectChanges();
              this.contentBusy = true;
              this.postsBusy = true;
              return this.getUserAdditionalInfoBySlugGQL
                .fetch({ slug: res?.data?.userBySlug?.slug }, { fetchPolicy: 'no-cache' })
                .pipe(map(r => mergeDeep(res.data?.userBySlug, r.data.userBySlug)));
            }
            return of(null);
          }),
          mergeMap(res => {
            if (res?.landIds?.length) {
              return this.landEssentialsGQL
                .fetch({ id: res.landIds[0] }, { fetchPolicy: 'no-cache' })
                .pipe(map(landRes => ({ ...res, land: landRes.data.land })));
            }
            return of(res);
          }),
        )
        .subscribe({
          next: user => {
            if (user) {
              this.fetchUser(user as UserExtended, true);
              this.contentBusy = false;
              this.postsBusy = false;
              this.cd.detectChanges();
            }
          },
        }),
    );
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    this.maxPictures = this.isMobileNotIpad()
      ? this.isCustomSmallScreen(367)
        ? 2
        : 3
      : this.isMobileIpad()
        ? 5
        : this.isCustomSmallScreen(1067)
          ? 4
          : this.isCustomSmallScreen(1152)
            ? 5
            : 6;
    this.cd.detectChanges();
  }

  observeHeader() {
    const picture = this.document.querySelector('#cherry-box-profile');
    const header = this.document.querySelector('#profile-header-content');
    if (!header) return null;
    const inViewport: IntersectionObserverCallback = (entries, _observer) => {
      entries.forEach(entry => {
        this.headerIntersecting = !entry.isIntersecting;
        this.cd.detectChanges();
      });
    };
    const Obs = new IntersectionObserver(inViewport, { threshold: 1 });
    Obs.observe(header);
    Obs.observe(picture);
    return header;
  }

  ngAfterViewInit() {
    if (this.isPlatformBrowser() && Number(this.queryParams?.tab) === 2) {
      inject(NgZone).runOutsideAngular(() =>
        setTimeout(
          () =>
            this.document
              .querySelector('.profile__activity')
              ?.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' }),
          10,
        ),
      );
    }
    if (this.isPlatformBrowser()) {
      let header;
      const timeout = setInterval(() => {
        header = this.observeHeader();
        if (header) clearInterval(timeout);
      }, 500);
    }
  }

  fetchUser(_user: UserExtended, isComplete: boolean) {
    const user = { ..._user };
    const prefixTitle = this.translateService.t(`${this.translateService.language}.profile.title`);
    this.metadataService.setTitle(`${prefixTitle} | ${this.userNameService.name(user)}`);
    this.metadataService.setDescription(user.whoIAm || user.bio);
    this.metadataService.setTags([
      { property: 'og:title', content: this.userNameService.name(user) },
      { property: 'og:url', content: baseUrl + this.location.path() },
      { property: 'og:description', content: user.whoIAm || user.bio },
      { property: 'og:image', content: cdn + '/user/' + this.slug + '/cover.thumbnail.jpg' },
      { property: 'og:image:url', content: cdn + '/user/' + this.slug + '/cover.thumbnail.jpg' },
      { property: 'og:image:width', content: '600' },
      { property: 'og:image:height', content: '600' },
      { property: 'og:image:type', content: 'image/*' },
    ]);
    if (user.plusAttributes && typeof user.plusAttributes === 'string')
      user.plusAttributes = JSON.parse(user.plusAttributes);
    if (user.contactWhatsapp) {
      user.contactWhatsapp = user.contactWhatsapp.split(' ').join('').replace('+', '');
    }
    if (user.contactPhone) {
      user.contactPhone = user.contactPhone.split(' ').join('');
    }
    if (user.followings) {
      user.userFollowings = user.followings.filter(f => f.following.__typename === 'User');
      user.followingsUser = user.followings
        .filter(f => f.following.__typename === 'User')
        .map(c => c.following as User);
    }
    if (user.clappings) {
      user.contentClappings = user.clappings.filter(f => !!f.content);
    }
    if (user.posterImage && typeof user.posterImage === 'string') {
      user.posterImage = JSON.parse(user.posterImage);
    }
    this.profileStore.setCompleteState(isComplete);
    this.profileStore.setContent(user);
    this.cd.detectChanges();
    if (Number(this.queryParams?.tab) === 2 && this.isPlatformBrowser()) {
      this.document
        .querySelector('.profile__activity')
        ?.scrollIntoView({ block: 'start', inline: 'start', behavior: 'smooth' });
    }
  }

  onShowGalleryChange(index?: number) {
    import('src/app/_shared/dialogs/gallery-contents-dialog/gallery-contents-dialog.component').then(
      ({ GalleryContentsDialogComponent }) =>
        this.openDialogService.openDialog(
          GalleryContentsDialogComponent,
          {
            autoFocus: false,
            restoreFocus: false,
            data: {
              title: '',
              type: 'profile',
              slug: this.profileStore?.state?.slug,
              index,
            },
          },
          true,
        ),
    );
  }

  onTranslate(slug: string, foreignId: string) {
    this.subscriptions.push(
      this.translationRequest
        .mutate({
          input: {
            foreignClass: 'User',
            foreignId,
            locale: this.translateService?.language as Locale,
          },
        })
        .pipe(
          mergeMap(r => {
            if (r?.data?.translationRequest?.success === OperationReturnTypeEnum.Ok) {
              return this.getUserBySlugTranslated.fetch({
                slug,
                locale: this.translateService.language as Locale,
              });
            }
            return of(null);
          }),
        )
        .subscribe(res => {
          if (res) {
            this.profileStore.setContent({ ...this.profileStore.state, ...res.data?.userBySlug });
          }
          this.seeTranslated = !this.seeTranslated;
          this.cd.detectChanges();
        }),
    );
  }

  onFollowersClick() {
    this.followerBusy = true;
    this.cd.detectChanges();
    this.subscriptions.push(
      this.getUserFollowersGQL
        .fetch({ slug: this.profileStore.state.slug, limit: 20, skip: 0 }, { fetchPolicy: 'no-cache' })
        .pipe(
          tap(res => {
            if (res.data.userBySlug.followers) {
              this.profileStore.setContent({
                ...this.profileStore.state,
                followers: res.data.userBySlug.followers as User[],
                followersCount: res.data.userBySlug.followersCount,
              });
              this.followerBusy = false;
              this.cd.detectChanges();
            }
          }),
          mergeMap(() => from(import('src/app/_shared/dialogs/people-dialog/people-dialog.component'))),
          mergeMap(({ PeopleDialogComponent }) =>
            this.openDialogService
              .openDialog(PeopleDialogComponent, {
                ...getDefaultModalSettings(),
                data: {
                  people: this.profileStore?.state?.followers.map(f => ({ user: { ...f } })),
                  headerText: this.translateService.t('profile.dialog-who-follows.title'),
                  type: 'followers',
                  slug: this.profileStore.state?.slug,
                },
              })
              .afterClosed(),
          ),
        )
        .subscribe({
          next: user => {
            if (user) {
              this.router.navigate([this.routeTranslateService.translateRoute('profile'), user.slug]);
            }
          },
        }),
    );
  }

  onFollowingsClick() {
    this.userFollowingsBusy = true;
    this.cd.detectChanges();
    this.subscriptions.push(
      this.getUserFollowingsGQL
        .fetch({ slug: this.profileStore.state.slug, limit: 20, skip: 0 }, { fetchPolicy: 'no-cache' })
        .pipe(
          tap(res => {
            if (res.data.userBySlug.followings) {
              this.profileStore.setContent({
                ...this.profileStore.state,
                followingsUser: res.data.userBySlug.followings.map(f => f.following as User),
                followingsCount: res.data.userBySlug.followingsCount,
              });
              this.userFollowingsBusy = false;
              this.cd.detectChanges();
            }
          }),
          mergeMap(() => from(import('src/app/_shared/dialogs/people-dialog/people-dialog.component'))),
          mergeMap(({ PeopleDialogComponent }) =>
            this.openDialogService
              .openDialog(PeopleDialogComponent, {
                ...getDefaultModalSettings(),
                data: {
                  people: this.profileStore?.state?.followingsUser.map(f => ({ user: { ...f } })),
                  headerText: this.translateService.t('profile.dialog-who-i-follow.title'),
                  type: 'followings',
                  slug: this.profileStore.state?.slug,
                },
              })
              .afterClosed(),
          ),
        )
        .subscribe({
          next: user => {
            if (user) {
              this.router.navigate([this.routeTranslateService.translateRoute('profile'), user.slug]);
            }
          },
        }),
    );
  }

  onFavouritesClick() {
    this.cd.detectChanges();
    this.subscriptions.push(
      from(import('src/app/pages/profile/content-dialog/content-dialog.component')).subscribe({
        next: ({ ContentDialogComponent }) => {
          this.openDialogService.openDialog(ContentDialogComponent, {
            ...getDefaultModalSettings(),
            data: {
              loadContents: true,
              userSlug: this.profileStore.state.slug,
              headerText: 'Cosa mi piace',
            },
          });
        },
      }),
    );
  }

  onWriteClick() {
    const fun = () => {
      const modalSettings = getDefaultModalSettings();
      modalSettings.width = this.window.matchMedia('(max-width: ' + ScreenSizes.SM + 'px)').matches ? '90vw' : '50vw';
      this.subscriptions.push(
        from(import('src/app/_shared/dialogs/write-users-dialog/write-users-dialog.component')).subscribe({
          next: ({ WriteUsersDialogComponent }) => {
            this.openDialogService.openDialog(WriteUsersDialogComponent, {
              ...modalSettings,
              data: {
                title: this.translateService.t('profile.write-user-dialog-title-pro'),
                subtitle: this.translateService.t('profile.write-user-dialog-subtitle-pro'),
                user: this.profileStore?.state,
              },
            });
          },
        }),
      );
    };
    this.notLoggedService.execute(() => fun());
  }

  onShowMoreInfo() {
    const fun = () => {
      this.usefulCollapsed = !this.usefulCollapsed;
    };
    this.notLoggedService.execute(() => fun());
  }

  onFollow() {
    const fun = () => {
      this.onFollowBusy = true;
      this.subscriptions.push(
        (this.profileStore.state?.myFollow
          ? this.unfollowFriendGQL
              .mutate({ id: this.profileStore.state.myFollow.id })
              .pipe(map(() => ({ isFollow: false, myFollow: undefined })))
          : this.followFriendGQL
              .mutate({ input: { contentId: this.profileStore.state.id, contentType: CarouselableEntityEnum.User } })
              .pipe(map(r => ({ isFollow: true, myFollow: r.data.followCreate })))
        )
          .pipe(
            catchError((err: any) => {
              this.onFollowBusy = false;
              this.cd.detectChanges();
              return throwError(() => err);
            }),
          )
          .subscribe({
            next: ({ isFollow, myFollow }) => {
              const user = this.userService.user;
              let followers: User[] = [...(this.profileStore?.state?.followers || [])];
              if (isFollow) {
                followers.push(user as User);
                this.gtagService.pushEvent({
                  event: 'community_interactions',
                  event_action: 'follow',
                  user_id: this.userService?.user?.id,
                });
              } else {
                followers = followers.filter(f => f.id !== this.userService.user.id);
              }
              this.profileStore.setContent({
                ...this.profileStore.state,
                followers,
                followersCount: this.profileStore.state.followersCount + (isFollow ? 1 : -1),
                myFollow: myFollow as Follow,
              });
              this.onFollowBusy = false;
              this.cd.detectChanges();
            },
          }),
      );
    };
    this.notLoggedService.execute(() => fun());
  }

  onCategorySearch(id: string) {
    this.searchService.categorySearch(
      id,
      this.profileStore?.state?.workAddress,
      this.profileStore?.state?.latitude,
      this.profileStore?.state?.longitude,
    );
  }

  onMailto(subject: string) {
    mailTelTo(this.window, subject);
  }

  onTel(subject: string) {
    mailTelTo(this.window, subject.replace(/\s*/g, ''), 'tel');
  }

  ngOnDestroy(): void {
    this.dialog.closeAll();
    super.ngOnDestroy();
    // this.metadataService.clean();
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
    this.limitSubj.complete();
  }
}

export type UserExtended = Partial<User> & {
  userFollowings: Follow[];
  followingsUser: User[];
  contentClappings: ContentLike[];
  proInfoSetted: boolean;
  land: {
    __typename?: 'Land';
    id: string;
    title?: string;
    slug?: string;
  };
  pictures: (UserGalleryImage & {
    image: Image & {
      fullsize: string;
    };
  })[];
  coverPicture: UserGalleryImage & {
    image: Image & {
      vw480: string;
      vw768: string;
      vw1024: string;
      fullsize: string;
    };
  };
};
